import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';

import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Stack,
  Select,
  Tooltip,
  Divider,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';

import Iconify from 'src/components/iconify';

import { styles } from '../styles';

TicketHeader.propTypes = {
  ticket: PropTypes.object,
  statuses: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onToggleRight: PropTypes.func.isRequired,
  onShowAnswers: PropTypes.func.isRequired,
  onShowFiles: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  isRightPanelOpen: PropTypes.bool.isRequired,
  hasUpdatePermission: PropTypes.bool,
};

export default function TicketHeader({
  ticket,
  statuses,
  onUpdate,
  onToggleRight,
  onShowAnswers,
  onShowFiles,
  onBack,
  isRightPanelOpen,
  hasUpdatePermission,
}) {
  const { t } = useTranslation();

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(ticket?.title || '');

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusHover, setStatusHover] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const priorityHover = false;

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const canEdit = hasUpdatePermission;

  useEffect(() => {
    if (ticket?.title) {
      setTempTitle(ticket.title);
    }
  }, [ticket?.title]);

  if (!ticket) {
    return (
      <Box sx={styles.actionHeader}>
        <Stack direction="row" spacing={2} sx={{ flexGrow: 1 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width="40%" height={40} />
        </Stack>
      </Box>
    );
  }

  const handleTitleSubmit = () => {
    if (tempTitle.trim() === ticket.title) {
      setIsEditingTitle(false);
      return;
    }
    onUpdate('title', tempTitle, 'Title');
    setIsEditingTitle(false);
  };

  const copyToClipboard = () => {
    if (ticket.id) navigator.clipboard.writeText(ticket.id.toString());
  };

  const colorMap = {
    success: '#229A16',
    error: '#B72136',
    warning: '#FFC107',
    info: '#2065D1',
  };

  const getSafeColor = (colorStr) => {
    if (!colorStr) return '#919EAB';
    const clean = colorStr.trim();
    if (colorMap[clean]) return colorMap[clean];
    if (colorMap[clean.toLowerCase()]) return colorMap[clean.toLowerCase()];
    if (/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(clean)) {
      return `#${clean}`;
    }
    return clean;
  };

  const currentStatusObj = statuses.find(
    (s) => Number(s.statusId) === Number(ticket.status_id)
  );

  const statusBgColor = getSafeColor(currentStatusObj?.color);
  const priorityBgColor =
    ticket.priority === 'HIGH'
      ? '#FF4842'
      : ticket.priority === 'MEDIUM'
      ? '#FFC107'
      : '#00A76F';

  const iconButtonStyle = {
    p: 0.5,
    transition: 'background-color 0.2s',
    cursor: 'pointer',
    '&:hover': { bgcolor: 'action.hover' },
    '&:active': { bgcolor: 'action.selected' },
  };

  const getMorphStyle = (bgColor) => ({
    borderRadius: isRightPanelOpen ? '50%' : '20px',
    width: isRightPanelOpen ? '22px' : 'fit-content',
    minWidth: isRightPanelOpen ? '22px' : '80px',
    height: isRightPanelOpen ? '22px' : '33px',
    fontSize: isRightPanelOpen ? '0px' : '0.85rem',
    fontWeight: 800,

    color: isRightPanelOpen ? '#FFFFFF' : isDark ? bgColor : '#FFFFFF',

    bgcolor: isRightPanelOpen ? bgColor : isDark ? alpha(bgColor, 0.2) : bgColor,

    border: !isRightPanelOpen && isDark ? `1.5px solid ${alpha(bgColor, 0.42)}` : 'none',
    cursor: canEdit ? 'pointer' : 'default',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isRightPanelOpen ? '0 !important' : '0 16px !important',
      height: isRightPanelOpen ? '20px' : '33px',
    },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '&:hover': {
      bgcolor: isRightPanelOpen ? bgColor : isDark ? alpha(bgColor, 0.2) : bgColor,
      opacity: 0.9,
    },
  });

  return (
    <Box sx={{ ...styles.actionHeader, backgroundColor: 'background.paper' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        <Stack direction="column" spacing={0.2} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ '&:hover .title-edit-btn': { opacity: 1 } }}
          >
            {isEditingTitle ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <TextField
                  variant="standard"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleTitleSubmit()}
                  sx={{
                    '& .MuiInputBase-input': { fontWeight: 700, fontSize: '1.25rem' },
                  }}
                />
                <Tooltip title={t('ticket.saveTitle')} placement="bottom">
                  <IconButton
                    size="small"
                    onClick={handleTitleSubmit}
                    sx={iconButtonStyle}
                  >
                    <Box
                      component="img"
                      src="/assets/icons/Tickets/Edit.svg"
                      sx={{
                        width: 30,
                        filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                        transition: 'filter 0.25s',
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('ticket.cancelEdit')} placement="bottom">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTempTitle(ticket.title);
                      setIsEditingTitle(false);
                    }}
                    sx={iconButtonStyle}
                  >
                    <Iconify
                      icon="solar:close-circle-bold"
                      color="error.main"
                      width={23}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <>
                <Typography
                  variant="h4"
                  noWrap
                  sx={{ fontWeight: 700, cursor: 'default', color: 'text.primary' }}
                >
                  {ticket.title}
                </Typography>
                {canEdit && (
                  <Tooltip title={t('ticket.editTitle')} placement="bottom">
                    <IconButton
                      className="title-edit-btn"
                      size="small"
                      onClick={() => setIsEditingTitle(true)}
                      sx={{ ...iconButtonStyle, opacity: 0, transition: 'opacity 0.2s' }}
                    >
                      <Box
                        component="img"
                        src="/assets/icons/Tickets/Edit.svg"
                        sx={{
                          width: 30,
                          filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                          transition: 'filter 0.25s',
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 600 }}
            >
              {t('ticket.ticketId')}: #{ticket.id}
            </Typography>
            <IconButton size="small" onClick={copyToClipboard} sx={iconButtonStyle}>
              <Box
                component="img"
                src="/assets/icons/Tickets/copy.svg"
                sx={{
                  width: 14,
                  filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                  transition: 'filter 0.25s',
                }}
              />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Tooltip
          title={canEdit ? t('ticket.status') : ''}
          placement="bottom"
          open={statusHover && !statusOpen && canEdit}
          onOpen={() => setStatusHover(true)}
          onClose={() => setStatusHover(false)}
        >
          <Select
            size="small"
            value={ticket.status_id}
            readOnly={!canEdit}
            onOpen={() => setStatusOpen(true)}
            onClose={() => setStatusOpen(false)}
            IconComponent={() => null}
            onChange={(e) => onUpdate('status_id', e.target.value, 'Status')}
            sx={getMorphStyle(statusBgColor)}
            renderValue={(value) => {
              const s = statuses.find((st) => Number(st.statusId) === Number(value));
              const label = s?.statusId <= 5 ? t(`status.${s?.name}`) : s?.name;
              return (
                <Box
                  sx={{ opacity: isRightPanelOpen ? 0 : 1, transition: 'opacity 0.2s' }}
                >
                  {!isRightPanelOpen && label}
                </Box>
              );
            }}
          >
            {statuses.map((s) => (
              <MenuItem key={s.statusId} value={s.statusId} sx={{ fontSize: '0.8rem' }}>
                {s.statusId <= 5 ? t(`status.${s?.name}`) : s.name}
              </MenuItem>
            ))}
          </Select>
        </Tooltip>

        <Tooltip
          title={canEdit ? t('ticket.priority') : ''}
          placement="bottom"
          open={priorityHover && !priorityOpen && canEdit}
          onOpen={() => setPriorityOpen(true)}
          onClose={() => setPriorityOpen(false)}
        >
          <Select
            size="small"
            value={ticket.priority}
            readOnly={!canEdit}
            onOpen={() => setPriorityOpen(true)}
            onClose={() => setPriorityOpen(false)}
            IconComponent={() => null}
            onChange={(e) => onUpdate('priority', e.target.value, 'Priority')}
            sx={getMorphStyle(priorityBgColor)}
            renderValue={(value) => (
              <Box sx={{ opacity: isRightPanelOpen ? 0 : 1, transition: 'opacity 0.2s' }}>
                {!isRightPanelOpen && t(`priority.${value}`)}
              </Box>
            )}
          >
            <MenuItem value="HIGH">{t('priority.HIGH')}</MenuItem>
            <MenuItem value="MEDIUM">{t('priority.MEDIUM')}</MenuItem>
            <MenuItem value="LOW">{t('priority.LOW')}</MenuItem>
          </Select>
        </Tooltip>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderRightWidth: 1, borderColor: 'divider', mx: 0.5 }}
        />

        <Stack alignItems="center" spacing={0}>
          <Tooltip title={t('ticket.messages')} placement="bottom">
            <IconButton onClick={onShowAnswers} sx={iconButtonStyle}>
              <Box
                component="img"
                src="/assets/icons/Tickets/form.svg"
                width={39}
                sx={{
                  filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                  transition: 'filter 0.25s',
                }}
              />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.65rem',
              fontWeight: 650,
              mt: -0.8,
              paddingTop: '5px',
            }}
          >
            {t('ticket.messages')}
          </Typography>
        </Stack>

        <Stack alignItems="center" spacing={0}>
          <Tooltip title={t('ticket.attachments')} placement="bottom">
            <IconButton onClick={onShowFiles} sx={iconButtonStyle}>
              <Box
                component="img"
                src="/assets/icons/Tickets/files.svg"
                width={37}
                sx={{
                  filter: isDark ? 'brightness(0) invert(0.9)' : 'none',
                  transition: 'filter 0.25s',
                }}
              />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.65rem',
              fontWeight: 650,
              mt: -0.8,
              paddingTop: '5px',
            }}
          >
            {t('ticket.attachments')}
          </Typography>
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          <Tooltip title={t('ticket.menu')} placement="bottom">
            <IconButton onClick={onToggleRight} sx={iconButtonStyle}>
              <Iconify
                icon="solar:alt-arrow-left-outline"
                width={32}
                sx={{
                  transform: isRightPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  color: 'text.primary',
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
    </Box>
  );
}
